import { useEffect, useMemo, useRef, useState } from 'react';
import { adminHeaders } from '../../lib/adminAuth';
import './MailTemplatesPage.css';

import { tx } from '../../lib/adminI18n';
/**
 * メールテンプレート管理
 *   - 種類 × 言語（ja/en/zh）ごとに件名・本文を編集
 *   - {{変数}} をクリックでカーソル位置に挿入
 *   - 右側に送信時と同じ HTML プレビュー（サンプル予約で差し込み）
 */

interface GuideSchedule { enabled: boolean; days_before: number; hour: number; }
interface MailSchedule {
  checkin_guide: GuideSchedule;
  checkout_guide: GuideSchedule;
  payment_reminder: { enabled: boolean; hours_before: number };
}
// 旧スケジュール箱は payment_reminder のみ担当（checkin/checkout は新トリガーUIへ移行）
const SCHEDULABLE = ['payment_reminder'];
const dayLabel = (n: number) => (n === 0 ? tx('mt.0') : n === 1 ? tx('mt.1') : tx('mt.2', { p0: n }));

// ===== 汎用送信トリガー（booking_confirmed / passport_upload / checkin_guide / checkout_guide） =====
type TriggerAnchor = 'confirmed' | 'checkin' | 'checkout';
interface MailTrigger {
  enabled: boolean; anchor: TriggerAnchor;
  offset_minutes: number; days_before: number; hour: number;
}
type MailTriggers = Record<string, MailTrigger>;
const TRIGGERABLE = ['booking_confirmed', 'passport_upload', 'checkin_guide', 'checkout_guide'];
// 種別ごとに選べる基準イベント（UIの選択肢）
const ANCHOR_OPTIONS: Record<string, { value: TriggerAnchor; label: string }[]> = {
  booking_confirmed: [{ value: 'confirmed', get label() { return tx('mt.3'); } }],
  passport_upload: [
    { value: 'confirmed', get label() { return tx('mt.3'); } },
    { value: 'checkin', get label() { return tx('mt.4'); } },
    { value: 'checkout', get label() { return tx('mt.5'); } },
  ],
  checkin_guide: [{ value: 'checkin', get label() { return tx('mt.4'); } }],
  checkout_guide: [{ value: 'checkout', get label() { return tx('mt.5'); } }],
};

interface Template { id: string; locale: string; type: string; subject: string; body: string; updated_at?: string; }

const TYPES: { key: string; label: string; desc: string }[] = [
  { key: 'booking_confirmed', get label() { return tx('mt.6'); }, get desc() { return tx('mt.7'); } },
  { key: 'transfer_instructions', get label() { return tx('mt.8'); }, get desc() { return tx('mt.9'); } },
  { key: 'payment_reminder', get label() { return tx('mt.10'); }, get desc() { return tx('mt.11'); } },
  { key: 'cancel_approved', get label() { return tx('mt.12'); }, get desc() { return tx('mt.13'); } },
  { key: 'checkin_guide', get label() { return tx('mt.14'); }, get desc() { return tx('mt.15'); } },
  { key: 'checkout_guide', get label() { return tx('mt.16'); }, get desc() { return tx('mt.17'); } },
  { key: 'passport_upload', get label() { return tx('mt.18'); }, get desc() { return tx('mt.19'); } },
];
const LOCALES = [{ key: 'ja', label: '日本語' }, { key: 'en', label: 'English' }, { key: 'zh', label: '中文' }];

const VARS: { key: string; label: string }[] = [
  { key: 'guest_name', get label() { return tx('mt.22'); } },
  { key: 'reservation_id', get label() { return tx('mt.23'); } },
  { key: 'checkin_date', get label() { return tx('mt.24'); } },
  { key: 'checkout_date', get label() { return tx('mt.25'); } },
  { key: 'checkin_time', get label() { return tx('mt.26'); } },
  { key: 'checkout_time', get label() { return tx('mt.27'); } },
  { key: 'guest_count', get label() { return tx('mt.28'); } },
  { key: 'total_price', get label() { return tx('mt.29'); } },
  { key: 'property_name', get label() { return tx('mt.30'); } },
  { key: 'property_url', get label() { return tx('mt.31'); } },
  { key: 'facility_name', get label() { return tx('mt.32'); } },
  { key: 'facility_email', get label() { return tx('mt.33'); } },
  { key: 'free_cancel_until', get label() { return tx('mt.34'); } },
  { key: 'transfer_deadline_hours', get label() { return tx('mt.35'); } },
  { key: 'bank_name', get label() { return tx('mt.36'); } },
  { key: 'bank_branch', get label() { return tx('mt.37'); } },
  { key: 'bank_account_type', get label() { return tx('mt.38'); } },
  { key: 'bank_account_number', get label() { return tx('mt.39'); } },
  { key: 'bank_account_name', get label() { return tx('mt.40'); } },
  { key: 'refund_message', get label() { return tx('mt.41'); } },
  { key: 'passport_upload_url', get label() { return tx('mt.42'); } },
];

function MailTemplatesPage({ embedded = false }: { embedded?: boolean } = {}) {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [type, setType] = useState(TYPES[0].key);
  const [locale, setLocale] = useState('ja');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [mobile, setMobile] = useState(false);
  const [testTo, setTestTo] = useState(() => localStorage.getItem('mt_test_to') || '');
  const [testing, setTesting] = useState(false);
  const [schedule, setSchedule] = useState<MailSchedule | null>(null);
  const [savedSchedule, setSavedSchedule] = useState<MailSchedule | null>(null);
  const [transferHours, setTransferHours] = useState(72);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [scheduleMsg, setScheduleMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const scheduleDirty = JSON.stringify(schedule) !== JSON.stringify(savedSchedule);
  // 汎用トリガー
  const [triggers, setTriggers] = useState<MailTriggers | null>(null);
  const [savedTriggers, setSavedTriggers] = useState<MailTriggers | null>(null);
  const [savingTriggers, setSavingTriggers] = useState(false);
  const [triggerMsg, setTriggerMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const triggersDirty = JSON.stringify(triggers) !== JSON.stringify(savedTriggers);
  const lastFocus = useRef<'subject' | 'body'>('body');
  const subjectRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const current = useMemo(() => templates.find(t => t.type === type && t.locale === locale), [templates, type, locale]);
  const dirty = (current?.subject || '') !== subject || (current?.body || '') !== body;

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/admin/mail-templates', { headers: adminHeaders() });
      if (!res.ok) throw new Error(tx('mt.43', { p0: res.status }));
      setTemplates(await res.json());
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  };
  const loadSchedule = async () => {
    try {
      const res = await fetch('/api/admin/mail-schedule', { headers: adminHeaders() });
      if (!res.ok) return;
      const d = await res.json();
      setSchedule(d.schedule); setSavedSchedule(d.schedule); setTransferHours(d.transfer_expire_hours || 72);
    } catch { /* 取得失敗時はタイミング欄を出さない */ }
  };
  const loadTriggers = async () => {
    try {
      const res = await fetch('/api/admin/mail-triggers', { headers: adminHeaders() });
      if (!res.ok) return;
      const d = await res.json();
      setTriggers(d.triggers); setSavedTriggers(d.triggers);
    } catch { /* 取得失敗時はトリガー欄を出さない */ }
  };
  useEffect(() => { load(); loadSchedule(); loadTriggers(); }, []);

  const saveTriggers = async () => {
    if (!triggers) return;
    setSavingTriggers(true); setTriggerMsg(null);
    try {
      const res = await fetch('/api/admin/mail-triggers', {
        method: 'PUT',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(triggers),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || tx('mt.44', { p0: res.status }));
      setTriggers(d.triggers); setSavedTriggers(d.triggers);
      setTriggerMsg({ ok: true, text: tx('mt.45') });
    } catch (e: any) { setTriggerMsg({ ok: false, text: e.message }); }
    finally { setSavingTriggers(false); }
  };

  const saveSchedule = async () => {
    if (!schedule) return;
    setSavingSchedule(true); setScheduleMsg(null);
    try {
      const res = await fetch('/api/admin/mail-schedule', {
        method: 'PUT',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(schedule),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || tx('mt.44', { p0: res.status }));
      setSchedule(d.schedule); setSavedSchedule(d.schedule);
      setScheduleMsg({ ok: true, text: tx('mt.45') });
    } catch (e: any) { setScheduleMsg({ ok: false, text: e.message }); }
    finally { setSavingSchedule(false); }
  };

  /** 種類一覧に出す送信タイミングの説明（設定値を反映） */
  const descOf = (key: string, fallback: string) => {
    // 新トリガー対象はトリガー設定を反映
    if (TRIGGERABLE.includes(key) && savedTriggers?.[key]) {
      const g = savedTriggers[key];
      if (!g.enabled) return tx('mt.46');
      if (g.anchor === 'confirmed') return tx('mt.47', { p0: g.offset_minutes });
      const base = g.anchor === 'checkin' ? tx('mt.48') : tx('mt.49');
      return tx('mt.50', { p0: base, p1: dayLabel(g.days_before), p2: g.hour });
    }
    if (!savedSchedule) return fallback;
    if (key === 'checkin_guide' || key === 'checkout_guide') {
      const g = savedSchedule[key];
      if (!g.enabled) return tx('mt.46');
      return tx('mt.50', { p0: key === 'checkin_guide' ? tx('mt.48') : tx('mt.49'), p1: dayLabel(g.days_before), p2: g.hour });
    }
    if (key === 'payment_reminder') {
      const r = savedSchedule.payment_reminder;
      return r.enabled ? tx('mt.51', { p0: r.hours_before }) : tx('mt.46');
    }
    return fallback;
  };

  // 種類・言語を切り替えたら編集欄を差し替え
  useEffect(() => {
    setSubject(current?.subject || '');
    setBody(current?.body || '');
    setNotice(''); setError('');
  }, [current, type, locale]);

  // プレビュー（入力が止まって 400ms 後に更新）
  useEffect(() => {
    const h = setTimeout(async () => {
      if (!subject && !body) { setPreviewHtml(''); setPreviewSubject(''); return; }
      try {
        const res = await fetch('/api/admin/mail-templates/preview', {
          method: 'POST',
          headers: adminHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({ type, locale, subject, body }),
        });
        if (res.ok) { const d = await res.json(); setPreviewHtml(d.html); setPreviewSubject(d.subject); }
      } catch { /* プレビュー失敗は無視 */ }
    }, 400);
    return () => clearTimeout(h);
  }, [type, locale, subject, body]);

  const switchTo = (nextType: string, nextLocale: string) => {
    if ((dirty || scheduleDirty || triggersDirty) && !window.confirm(tx('mt.52'))) return;
    if (scheduleDirty) { setSchedule(savedSchedule); setScheduleMsg(null); }
    if (triggersDirty) { setTriggers(savedTriggers); setTriggerMsg(null); }
    setType(nextType); setLocale(nextLocale);
  };

  const insertVar = (key: string) => {
    const token = `{{${key}}}`;
    const el = lastFocus.current === 'subject' ? subjectRef.current : bodyRef.current;
    const val = lastFocus.current === 'subject' ? subject : body;
    const set = lastFocus.current === 'subject' ? setSubject : setBody;
    const start = el?.selectionStart ?? val.length, end = el?.selectionEnd ?? val.length;
    set(val.slice(0, start) + token + val.slice(end));
    requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange(start + token.length, start + token.length); });
  };

  const save = async () => {
    setSaving(true); setError(''); setNotice('');
    try {
      const res = await fetch('/api/admin/mail-templates', {
        method: 'PUT',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ type, locale, subject, body }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || tx('mt.44', { p0: res.status }));
      setNotice(tx('mt.53'));
      await load();
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTesting(true); setError(''); setNotice('');
    try {
      localStorage.setItem('mt_test_to', testTo.trim());
      const res = await fetch('/api/admin/mail-templates/test', {
        method: 'POST',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ type, locale, subject, body, to: testTo.trim() }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || tx('mt.54', { p0: res.status }));
      setNotice(tx('mt.55', { p0: testTo.trim() }));
    } catch (e: any) { setError(e.message); }
    finally { setTesting(false); }
  };

  const typeInfo = TYPES.find(t => t.key === type)!;
  const has = (tk: string, lk: string) => templates.some(t => t.type === tk && t.locale === lk);

  return (
    <div className="mt-page">
      <div className="mt-header">
        {!embedded && <h1>{tx('mt.56')}</h1>}
        <p className="mt-sub">{tx('mt.57')}</p>
      </div>

      {loading ? <p className="mt-empty">{tx('mt.58')}</p> : (
        <div className="mt-grid">
          {/* 左: 種類一覧 */}
          <nav className="mt-types">
            {TYPES.map(t => (
              <button key={t.key} className={`mt-type ${t.key === type ? 'active' : ''}`} onClick={() => switchTo(t.key, locale)}>
                <span className="mt-type-label">{t.label}</span>
                <span className="mt-type-desc">{descOf(t.key, t.desc)}</span>
                <span className="mt-type-langs">
                  {LOCALES.map(l => <span key={l.key} className={`mt-dot ${has(t.key, l.key) ? 'on' : ''}`}>{l.key}</span>)}
                </span>
              </button>
            ))}
          </nav>

          {/* 中央: 編集 */}
          <section className="mt-editor">
            <div className="mt-tabs">
              {LOCALES.map(l => (
                <button key={l.key} className={`mt-tab ${l.key === locale ? 'active' : ''}`} onClick={() => switchTo(type, l.key)}>
                  {l.label}{!has(type, l.key) && <span className="mt-new">{tx('mt.59')}</span>}
                </button>
              ))}
            </div>

            <h2 className="mt-title">{typeInfo.label}<small>{descOf(type, typeInfo.desc)}</small></h2>

            {triggers && TRIGGERABLE.includes(type) && (() => {
              const g = triggers[type];
              const anchors = ANCHOR_OPTIONS[type] || [];
              const setG = (patch: Partial<MailTrigger>) =>
                setTriggers({ ...triggers, [type]: { ...g, ...patch } });
              return (
                <div className="mt-schedule">
                  <div className="mt-schedule-head">
                    <span className="mt-schedule-title">{tx('mt.60')}</span>
                    <label className="mt-toggle">
                      <input type="checkbox" checked={g.enabled}
                        onChange={e => setG({ enabled: e.target.checked })} />
                      {tx('mt.61')}
                    </label>
                  </div>

                  <div className="mt-schedule-row">
                    {tx('mt.62')}
                    <select className="mt-select" value={g.anchor} disabled={!g.enabled}
                      onChange={e => setG({ anchor: e.target.value as TriggerAnchor })}>
                      {anchors.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                    </select>
                  </div>

                  {g.anchor === 'confirmed' ? (
                    <div className="mt-schedule-row">
                      {tx('mt.63')}
                      <select className="mt-select" value={g.offset_minutes} disabled={!g.enabled}
                        onChange={e => setG({ offset_minutes: Number(e.target.value) })}>
                        {[0, 5, 10, 15, 30, 60, 120, 180, 360, 720, 1440, 2880].map(m => (
                          <option key={m} value={m}>{m === 0 ? tx('mt.64') : m >= 60 ? tx('mt.65', { p0: m / 60 }) : tx('mt.66', { p0: m })}</option>
                        ))}
                      </select>
                      {tx('mt.67')}
                    </div>
                  ) : (
                    <div className="mt-schedule-row">
                      {g.anchor === 'checkin' ? tx('mt.48') : tx('mt.49')}{tx('mt.68')}
                      <select className="mt-select" value={g.days_before} disabled={!g.enabled}
                        onChange={e => setG({ days_before: Number(e.target.value) })}>
                        {[0, 1, 2, 3, 5, 7, 14].map(d => <option key={d} value={d}>{dayLabel(d)}</option>)}
                      </select>
                      {tx('mt.68')}
                      <select className="mt-select" value={g.hour} disabled={!g.enabled}
                        onChange={e => setG({ hour: Number(e.target.value) })}>
                        {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{h}:00</option>)}
                      </select>
                      {tx('mt.69')}
                    </div>
                  )}

                  <p className="mt-schedule-note">
                    {tx('mt.70')}
                    {type === 'passport_upload' && tx('mt.71')}
                  </p>
                  {triggerMsg && <div className={triggerMsg.ok ? 'msg-notice' : 'msg-error'}>{triggerMsg.text}</div>}
                  <div className="mt-actions">
                    <button className="mt-btn ghost" disabled={!triggersDirty || savingTriggers}
                      onClick={() => { setTriggers(savedTriggers); setTriggerMsg(null); }}>{tx('mt.72')}</button>
                    <button className="mt-btn primary" disabled={!triggersDirty || savingTriggers} onClick={saveTriggers}>
                      {savingTriggers ? tx('mt.73') : tx('mt.74')}
                    </button>
                  </div>
                </div>
              );
            })()}

            {schedule && SCHEDULABLE.includes(type) && (
              <div className="mt-schedule">
                <div className="mt-schedule-head">
                  <span className="mt-schedule-title">{tx('mt.60')}</span>
                  <label className="mt-toggle">
                    <input type="checkbox"
                      checked={type === 'payment_reminder' ? schedule.payment_reminder.enabled : schedule[type as 'checkin_guide'].enabled}
                      onChange={e => {
                        const enabled = e.target.checked;
                        setSchedule(type === 'payment_reminder'
                          ? { ...schedule, payment_reminder: { ...schedule.payment_reminder, enabled } }
                          : { ...schedule, [type]: { ...schedule[type as 'checkin_guide'], enabled } });
                      }} />
                    {tx('mt.61')}
                  </label>
                </div>

                {type === 'payment_reminder' ? (
                  <div className="mt-schedule-row">
                    {tx('mt.75')}
                    <select className="mt-select" value={schedule.payment_reminder.hours_before}
                      disabled={!schedule.payment_reminder.enabled}
                      onChange={e => setSchedule({ ...schedule, payment_reminder: { ...schedule.payment_reminder, hours_before: Number(e.target.value) } })}>
                      {[1, 2, 3, 6, 12, 18, 24, 36, 48].filter(h => h < transferHours).map(h => <option key={h} value={h}>{h}{tx('mt.76')}</option>)}
                    </select>
                    {tx('mt.67')}
                    <span className="mt-schedule-note">{tx('mt.77')} {transferHours} {tx('mt.78')}</span>
                  </div>
                ) : (
                  <div className="mt-schedule-row">
                    {type === 'checkin_guide' ? tx('mt.48') : tx('mt.49')}{tx('mt.68')}
                    <select className="mt-select" value={schedule[type as 'checkin_guide'].days_before}
                      disabled={!schedule[type as 'checkin_guide'].enabled}
                      onChange={e => setSchedule({ ...schedule, [type]: { ...schedule[type as 'checkin_guide'], days_before: Number(e.target.value) } })}>
                      {[0, 1, 2, 3, 5, 7, 14].map(d => <option key={d} value={d}>{dayLabel(d)}</option>)}
                    </select>
                    {tx('mt.68')}
                    <select className="mt-select" value={schedule[type as 'checkin_guide'].hour}
                      disabled={!schedule[type as 'checkin_guide'].enabled}
                      onChange={e => setSchedule({ ...schedule, [type]: { ...schedule[type as 'checkin_guide'], hour: Number(e.target.value) } })}>
                      {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{h}:00</option>)}
                    </select>
                    {tx('mt.69')}
                  </div>
                )}

                <p className="mt-schedule-note">
                  {tx('mt.79')}
                  {type !== 'payment_reminder' && tx('mt.80')}
                </p>
                {scheduleMsg && <div className={scheduleMsg.ok ? 'msg-notice' : 'msg-error'}>{scheduleMsg.text}</div>}
                <div className="mt-actions">
                  <button className="mt-btn ghost" disabled={!scheduleDirty || savingSchedule}
                    onClick={() => { setSchedule(savedSchedule); setScheduleMsg(null); }}>{tx('mt.72')}</button>
                  <button className="mt-btn primary" disabled={!scheduleDirty || savingSchedule} onClick={saveSchedule}>
                    {savingSchedule ? tx('mt.73') : tx('mt.74')}
                  </button>
                </div>
              </div>
            )}

            <label className="mt-label">{tx('mt.81')}</label>
            <input ref={subjectRef} className="mt-input" value={subject}
              onFocus={() => (lastFocus.current = 'subject')} onChange={e => setSubject(e.target.value)} />

            <label className="mt-label">{tx('mt.82')}</label>
            <textarea ref={bodyRef} className="mt-textarea" value={body} rows={22}
              onFocus={() => (lastFocus.current = 'body')} onChange={e => setBody(e.target.value)} />

            <p className="mt-hint">
              {tx('mt.83')}<code>■見出し</code> {tx('mt.85')} <code>【見出し】</code> {tx('mt.87')}
              <code>ラベル: 値</code> {tx('mt.89')}<code>━━━━</code> {tx('mt.90')}
            </p>

            <div className="mt-vars">
              <span className="mt-vars-title">{tx('mt.91')}</span>
              {VARS.map(v => (
                <button key={v.key} type="button" className="mt-var" title={`{{${v.key}}}`} onClick={() => insertVar(v.key)}>{v.label}</button>
              ))}
            </div>

            <div className="mt-test">
              <label className="mt-label">{tx('mt.92')}</label>
              <div className="mt-test-row">
                <input className="mt-input" type="email" placeholder="test@example.com" value={testTo} onChange={e => setTestTo(e.target.value)} />
                <button className="mt-btn ghost" disabled={testing || !testTo.trim() || !subject.trim() || !body.trim()} onClick={sendTest}>
                  {testing ? tx('mt.93') : tx('mt.94')}
                </button>
              </div>
            </div>

            {error && <div className="msg-error">{error}</div>}
            {notice && <div className="msg-notice">{notice}</div>}

            <div className="mt-actions">
              {current?.updated_at && <span className="mt-updated">{tx('mt.95')} {current.updated_at}</span>}
              <button className="mt-btn ghost" disabled={!dirty || saving}
                onClick={() => { setSubject(current?.subject || ''); setBody(current?.body || ''); }}>{tx('mt.72')}</button>
              <button className="mt-btn primary" disabled={!dirty || saving || !subject.trim() || !body.trim()} onClick={save}>
                {saving ? tx('mt.73') : tx('mt.96')}
              </button>
            </div>
          </section>

          {/* 右: プレビュー */}
          <section className="mt-preview">
            <div className="mt-preview-bar">
              <span>{tx('mt.97')}</span>
              <span className="mt-switch">
                <button className={!mobile ? 'on' : ''} onClick={() => setMobile(false)}>PC</button>
                <button className={mobile ? 'on' : ''} onClick={() => setMobile(true)}>{tx('mt.98')}</button>
              </span>
            </div>
            {previewSubject && <div className="mt-preview-subject"><b>{tx('mt.99')}</b> {previewSubject}</div>}
            <div className="mt-frame-wrap">
              <iframe title="preview" className="mt-frame" style={{ width: mobile ? 390 : '100%' }} srcDoc={previewHtml} sandbox="" />
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

export default MailTemplatesPage;
